/**
 * Copyright © 2026 Adnuntius AS.
 */
import {HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';

export interface AdnHttpRequest {
  method: string;
  url: string;
  params?: Record<string, any>;
  headers?: Record<string, any>;
  body?: any;
  responseType?: 'json' | 'text' | 'blob';
  cancel?: Promise<any>;
  cache?: boolean;
  hasErrorHandler?: boolean;
  ignore404?: boolean;
  /** Skip AuthHttpBufferService 401 buffering (auth endpoints, explicit opt-out). */
  ignoreAuthModule?: boolean;
  /** Keep an explicitly supplied context instead of replacing it with the current UI network. */
  preserveContext?: boolean;
}

export interface AdnHttpResponse {
  status: number;
  data: any;
  config: AdnHttpRequest;
  xhrStatus?: 'abort';
}

export interface AdnHttpCanceller {
  promise: Promise<any>;
  resolve: (reason?: any) => void;
}

export type AdnHttpProgressCallback = (event: {loaded: number; total?: number}) => void;

export function createAdnUploadFormData(data: Record<string, any>): FormData {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (value instanceof Blob) {
      formData.append(key, value, (value as File).name);
    } else if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
  return formData;
}

export function createAdnHttpCanceller(): AdnHttpCanceller {
  let resolve: (reason?: any) => void = () => undefined;
  const promise = new Promise<any>((promiseResolve) => {
    resolve = promiseResolve;
  });
  return {promise, resolve};
}

function toHttpParams(params?: Record<string, any>): HttpParams {
  let httpParams = new HttpParams();
  const serialise = (value: any): string => {
    if (value instanceof Date) {
      return value.toISOString();
    }
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  };
  Object.keys(params || {}).forEach((key) => {
    const value = (params as Record<string, any>)[key];
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null) {
            httpParams = httpParams.append(key, serialise(item));
          }
        });
      } else {
        httpParams = httpParams.set(key, serialise(value));
      }
    }
  });
  return httpParams;
}

function toHttpHeaders(headers?: Record<string, any>): HttpHeaders {
  let httpHeaders = new HttpHeaders();
  Object.keys(headers || {}).forEach((key) => {
    const value = (headers as Record<string, any>)[key];
    if (value !== undefined && value !== null) {
      httpHeaders = httpHeaders.set(key, String(value));
    }
  });
  return httpHeaders;
}

/**
 * Pure Angular HTTP transport shared by resources upgraded off adnResource — no
 * $http/$resource/$q. Auth-header/context injection and API error handling stay
 * app-wide concerns, composed by AdnApiClientService rather than duplicated here.
 */
@Injectable({
  providedIn: 'root'
})
export class AdnHttpService {
  constructor(private readonly http: HttpClient) {
  }

  /**
   * Native Observable transport for Angular-first API services. Unlike the
   * compatibility Promise API, subscribers can feed signals directly and do
   * not need a global view-refresh pass when a request settles.
   */
  request$(config: AdnHttpRequest): Observable<AdnHttpResponse> {
    const requestOptions: any = {
      params: toHttpParams(config.params),
      headers: toHttpHeaders(config.headers),
      body: config.body,
      observe: 'response',
      responseType: config.responseType || 'json'
    };

    return new Observable<AdnHttpResponse>((subscriber) => {
      let settled = false;
      const subscription = this.http.request(config.method, config.url, requestOptions).subscribe({
        next: (response: any) => {
          settled = true;
          subscriber.next({status: response.status, data: response.body, config});
        },
        error: (err: HttpErrorResponse) => {
          settled = true;
          subscriber.error({status: err.status, data: err.error, config});
        },
        complete: () => subscriber.complete()
      });

      config.cancel?.then((reason: any) => {
        if (!settled) {
          settled = true;
          subscription.unsubscribe();
          subscriber.error({status: -1, data: reason, config, xhrStatus: 'abort'});
        }
      }, () => undefined);

      return () => subscription.unsubscribe();
    });
  }

  upload$(config: AdnHttpRequest, onProgress?: AdnHttpProgressCallback): Observable<AdnHttpResponse> {
    const requestOptions: any = {
      params: toHttpParams(config.params),
      headers: toHttpHeaders(config.headers),
      body: config.body,
      observe: 'events',
      reportProgress: true,
      responseType: config.responseType || 'json'
    };

    return new Observable<AdnHttpResponse>((subscriber) => {
      let settled = false;
      const subscription = this.http.request(config.method, config.url, requestOptions).subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            onProgress?.({
              loaded: event.loaded,
              total: event.total
            });
          } else if (event.type === HttpEventType.Response) {
            settled = true;
            subscriber.next({status: event.status, data: event.body, config});
            subscriber.complete();
          }
        },
        error: (err: HttpErrorResponse) => {
          settled = true;
          subscriber.error({status: err.status, data: err.error, config});
        }
      });

      if (config.cancel) {
        config.cancel.then((reason: any) => {
          if (!settled) {
            settled = true;
            subscription.unsubscribe();
            subscriber.error({status: -1, data: reason, config, xhrStatus: 'abort'});
          }
        }, () => undefined);
      }
      return () => subscription.unsubscribe();
    });
  }
}
